import { Component, DestroyRef, effect, inject, Injector, input, OnInit } from '@angular/core';
import { GenericTableComponent, TableColumn } from '../../core/generic-table/generic-table.component';
import { OrgAdminEndpointsService } from '../../../services/org-service/org-admin-endpoints.service';
import { finalize, forkJoin, map, Observable, of, switchMap, tap } from 'rxjs';
import { EventReportDetails } from '../org-model/event-report-details';
import { SearchbarComponent } from '../../../common/components/searchbar/searchbar.component';
import { MatOption, MatSelect, MatSelectChange } from '@angular/material/select';
import { AsyncPipe, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TonalButtonComponent } from '../../core/tonal-button/tonal-button.component';
import { MatFormField } from '@angular/material/form-field';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TableSettingsComponent } from '../../core/table-settings/table-settings.component';
import { OrgReportedEventsTestIds } from './org-reported-events.test-ids';

export interface ReportedEvent {
  reason: string;
  reporter: string;
  reporterId: string;
  reportedUser: string;
  reportedUserId: string;
  room: string;
  timestamp: number;
  reportedMessage: string;
}

@Component({
  selector: 'app-org-reported-events',
  imports: [
    GenericTableComponent,
    SearchbarComponent,
    AsyncPipe,
    FormsModule,
    TonalButtonComponent,
    DatePipe,
    MatSelect,
    MatOption,
    MatFormField,
    TableSettingsComponent
  ],
  templateUrl: './org-reported-events.component.html',
  styleUrl: './org-reported-events.component.scss'
})
/**
 * BEGRÜNDUNG: Es handelt sich um eine Angular-Komponente, weshalb keine AFO erfüllt wird.
 */
export class OrgReportedEventsComponent implements OnInit {
  protected readonly testIds = OrgReportedEventsTestIds;
  displayedColumns: TableColumn<EventReportDetails>[] = [];
  columsToDisplay: TableColumn<EventReportDetails>[] = [];
  dataSource$: Observable<EventReportDetails[]> = of([]);
  domain = input.required<string>();
  total = 0;
  pageSize = 10;
  pageIndex = 0;
  dir = 'b';
  userId = '';
  isLoading = false;
  selectedReports = new Set<EventReportDetails>();
  filterKey = '';
  private readonly orgadminEndpoints = inject(OrgAdminEndpointsService);
  private readonly destroy = inject(DestroyRef);
  private readonly injector = inject(Injector);

  ngOnInit(): void {
    this.displayedColumns = [
      { key: 'reason', label: 'Meldegrund', sortable: false, type: 'checkbox' },
      {
        key: 'user_id',
        label: 'TI-M Adresse Meldender',
        sortable: false
      },
      {
        key: 'sender',
        label: 'TI-M Adresse Gemeldeter',
        sortable: false
      },
      { key: 'name', label: 'Raum', sortable: false },
      {
        key: 'received_ts',
        label: 'Zeitstempel',
        type: 'date',
        sortable: true
      },
      { key: 'score', label: 'Schweregrad', sortable: false }
    ];
    this.columsToDisplay = this.displayedColumns.slice();
    effect(
      () => {
        this.loadReports();
      },
      { injector: this.injector }
    );
  }

  loadReports(): void {
    this.isLoading = true;
    const queryParams: Record<string, string | number | undefined> = {
      user_id: this.userId?.trim(),
      limit: this.pageSize,
      from: this.pageIndex * this.pageSize,
      dir: this.dir
    };
    this.dataSource$ = this.orgadminEndpoints.getEventReports(this.domain(), queryParams).pipe(
      tap(resp => {
        const result = resp.result;
        this.total = result.total;
      }),
      switchMap(resp => {
        const reports = resp.result.event_reports;
        if (!reports || reports.length === 0) {
          return of([]);
        }
        const detailRequests = reports.map(report =>
          this.orgadminEndpoints.getEventReportsDetails(this.domain(), String(report.id ?? ''))
        );
        return forkJoin(detailRequests);
      }),
      map(results => {
        return results.map(it => it?.result).filter(result => !!result);
      }),
      finalize(() => {
        this.isLoading = false;
      })
    );
  }

  onPageChange(event: { pageIndex: number; pageSize: number }): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.loadReports();
  }

  onSortChange(event: { dir: string }): void {
    if (event.dir === 'asc') {
      this.dir = 'f';
    } else {
      this.dir = 'b';
    }
    this.pageIndex = 0;
    this.loadReports();
  }

  getRowDetails(event: { element: EventReportDetails; isChecked: boolean }): void {
    if (event.isChecked) {
      this.selectedReports.add(event.element);
    } else {
      this.selectedReports.delete(event.element);
    }
  }

  setFilter(event: MatSelectChange): void {
    this.filterKey = event.value;
  }

  onSearchTerms(event: string): void {
    this.pageIndex = 0;

    if (this.filterKey === 'user_id') {
      this.userId = event;
      if (event.length >= 3) {
        this.loadReports();
      }
    }

    if (event.length === 0) {
      this.loadReports();
    }
  }

  deleteReports(): void {
    for (const selectedReport of this.selectedReports) {
      this.orgadminEndpoints
        .deleteEventReportsDetails(this.domain(), selectedReport.id!.toString())
        .pipe(takeUntilDestroyed(this.destroy))
        .subscribe(() => {
          this.selectedReports.delete(selectedReport);
          this.loadReports();
        });
    }
  }
}
